import * as React from 'react';

import type { Row } from '@tanstack/react-table';

/** Must stay in step with the CSS animation duration (`index.css`), plus slack for the last frame. */
const ROW_LEAVE_DURATION_MS = 200;

export type RowAnimationPhase = 'enter' | 'leave' | null;

export interface AnimatedRowEntry<TData> {
  row: Row<TData>;
  phase: RowAnimationPhase;
}

interface LeavingRow<TData> {
  row: Row<TData>;
  /** Id of the row that sat directly above this one in the last render it was part of. */
  predecessorId: string;
}

interface UseRowEnterLeaveAnimationParams<TData> {
  rows: Row<TData>[];
  /** Rows that take part in the animation. Undefined disables the whole feature. */
  shouldAnimate?: (row: TData) => boolean;
}

/**
 * Turns the current row model into a render list with an animation phase per row.
 *
 * Enter: a row animates only on the render it first appears in, so a re-render of an already
 * open sub-row (typing in one of its inputs) does not replay the grow-in. Nothing animates on the
 * very first render — the initial page load should not.
 *
 * Leave: TanStack drops collapsed sub-rows from the row model synchronously, so the previous
 * render's animated rows are remembered and the ones that vanished are kept for one animation
 * cycle, positioned after the row that preceded them. Detection runs in a layout effect and the
 * state update flushes before paint, so the rows never blink out and back in.
 */
export function useRowEnterLeaveAnimation<TData>({
  rows,
  shouldAnimate,
}: UseRowEnterLeaveAnimationParams<TData>): AnimatedRowEntry<TData>[] {
  const previousAnimatedRowsRef = React.useRef<LeavingRow<TData>[] | null>(null);
  const [leavingRows, setLeavingRows] = React.useState<LeavingRow<TData>[]>([]);
  const removalTimersRef = useRemovalTimers();

  const currentIds = React.useMemo(() => new Set(rows.map(row => row.id)), [rows]);
  const previousIds = React.useMemo(
    () => new Set((previousAnimatedRowsRef.current ?? []).map(entry => entry.row.id)),
    // The ref is rewritten in the layout effect below; `rows` is the signal that it changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows]
  );

  React.useLayoutEffect(() => {
    if (!shouldAnimate) return;

    const previous = previousAnimatedRowsRef.current ?? [];
    const gone = previous.filter(entry => !currentIds.has(entry.row.id));

    previousAnimatedRowsRef.current = rows.flatMap((row, index) =>
      shouldAnimate(row.original) && index > 0 ? [{ row, predecessorId: rows[index - 1].id }] : []
    );

    if (gone.length === 0) return;

    const goneIds = new Set(gone.map(entry => entry.row.id));
    setLeavingRows(current => [...current.filter(entry => !goneIds.has(entry.row.id)), ...gone]);

    const timer = window.setTimeout(() => {
      removalTimersRef.current.delete(timer);
      setLeavingRows(current => current.filter(entry => !goneIds.has(entry.row.id)));
    }, ROW_LEAVE_DURATION_MS);
    removalTimersRef.current.add(timer);
  }, [rows, currentIds, shouldAnimate, removalTimersRef]);

  return React.useMemo(() => {
    if (!shouldAnimate) return rows.map(row => ({ row, phase: null }));

    const isFirstRender = previousAnimatedRowsRef.current === null;
    const leavingByPredecessor = groupLeavingRowsByPredecessor(leavingRows, currentIds);

    return rows.flatMap((row): AnimatedRowEntry<TData>[] => {
      const isEntering = !isFirstRender && shouldAnimate(row.original) && !previousIds.has(row.id);
      return [
        { row, phase: isEntering ? 'enter' : null },
        ...collectLeavingRowsAfter(row.id, leavingByPredecessor).map(
          (leaving): AnimatedRowEntry<TData> => ({ row: leaving, phase: 'leave' })
        ),
      ];
    });
    // `previousIds` is derived from the ref and already keyed on `rows`.
  }, [rows, leavingRows, currentIds, previousIds, shouldAnimate]);
}

/**
 * Removal timers live outside the layout effect: a cleanup-based timer would be cancelled by the
 * next rows change (collapse one row, expand another within the animation) and the leaving rows
 * would never be removed. They are cleared only on unmount.
 */
function useRemovalTimers() {
  const timersRef = React.useRef<Set<number>>(new Set());
  React.useEffect(() => {
    const timers = timersRef.current;
    return () => timers.forEach(timer => window.clearTimeout(timer));
  }, []);
  return timersRef;
}

/** A row that came back (re-expanded mid-animation) is rendered live, so it is left out here. */
function groupLeavingRowsByPredecessor<TData>(
  leavingRows: LeavingRow<TData>[],
  currentIds: Set<string>
): Map<string, Row<TData>[]> {
  const byPredecessor = new Map<string, Row<TData>[]>();
  for (const { row, predecessorId } of leavingRows) {
    if (currentIds.has(row.id)) continue;
    byPredecessor.set(predecessorId, [...(byPredecessor.get(predecessorId) ?? []), row]);
  }
  return byPredecessor;
}

/**
 * Leaving rows are chained: a collapsed parent's sub-rows each sat after the previous one, so
 * collecting "everything that followed X" recursively restores the original order.
 */
function collectLeavingRowsAfter<TData>(
  rowId: string,
  leavingByPredecessor: Map<string, Row<TData>[]>
): Row<TData>[] {
  const leaving = leavingByPredecessor.get(rowId);
  if (!leaving) return [];
  return leaving.flatMap(row => [row, ...collectLeavingRowsAfter(row.id, leavingByPredecessor)]);
}
